"use client"

import * as React from "react"
import { LOCALE_COOKIE, resolveLocale, type Locale } from "@shared/i18n"
import { loadMessages } from "@/lib/i18n/messages"

type MessageTree = Record<string, unknown>

function lookup(tree: MessageTree, path: string[]): unknown {
  let node: unknown = tree
  for (const key of path) {
    if (node && typeof node === "object" && key in (node as object)) {
      node = (node as MessageTree)[key]
    } else {
      return undefined
    }
  }
  return node
}

/** Format a message string, replacing {var} placeholders with values. */
function formatMessage(
  message: string,
  values?: Record<string, string | number>,
): string {
  if (!values) return message
  return message.replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key]
    return value === undefined ? match : String(value)
  })
}

export type Translator = (
  key: string,
  values?: Record<string, string | number>,
) => string

type I18nContextValue = {
  locale: Locale
  t: Translator
  messages: MessageTree
}

const I18nContext = React.createContext<I18nContextValue | null>(null)

function readLocaleCookie(): Locale {
  if (typeof document === "undefined") {
    return "ko"
  }
  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${LOCALE_COOKIE}=`))
    ?.split("=")[1]
  return resolveLocale(value ?? null, navigator.language ?? null)
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale] = React.useState<Locale>(readLocaleCookie)
  const [messages, setMessages] = React.useState<MessageTree>({})

  React.useEffect(() => {
    let active = true
    loadMessages(locale).then((loaded) => {
      if (active) setMessages(loaded)
    })
    return () => {
      active = false
    }
  }, [locale])

  const t = React.useCallback<Translator>(
    (key, values) => {
      const value = lookup(messages, key.split("."))
      if (typeof value === "string") {
        return formatMessage(value, values)
      }
      return key
    },
    [messages],
  )

  const contextValue = React.useMemo(
    () => ({ locale, t, messages }),
    [locale, t, messages],
  )

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  )
}

export function useLocale(): Locale {
  const ctx = React.useContext(I18nContext)
  if (!ctx) throw new Error("useLocale must be used within I18nProvider")
  return ctx.locale
}

/**
 * next-intl-compatible client hook. `useTranslations("nav")` returns a
 * translator scoped to the "nav" namespace, so `t("brand")` resolves the
 * `nav.brand` message.
 */
export function useTranslations(namespace: string): Translator {
  const ctx = React.useContext(I18nContext)
  if (!ctx) throw new Error("useTranslations must be used within I18nProvider")
  return React.useCallback(
    (key: string, values?: Record<string, string | number>) => {
      const value = lookup(ctx.messages, `${namespace}.${key}`.split("."))
      if (typeof value === "string") {
        return formatMessage(value, values)
      }
      return key
    },
    [ctx.messages, namespace],
  )
}

/** Persist a locale change and update client state. */
export async function setLocaleClient(locale: Locale): Promise<void> {
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; SameSite=Lax; Max-Age=${60 * 60 * 24 * 365}`
  window.location.reload()
}

type DateTimeOptions = Intl.DateTimeFormatOptions
type NumberOptions = Intl.NumberFormatOptions

export type Formatter = {
  dateTime: (date: Date | number | string, options?: DateTimeOptions) => string
  number: (value: number, options?: NumberOptions) => string
}

/**
 * next-intl-compatible `useFormatter` hook backed by the browser's Intl API.
 * Formatting follows the currently active locale.
 */
export function useFormatter(): Formatter {
  const ctx = React.useContext(I18nContext)
  if (!ctx) throw new Error("useFormatter must be used within I18nProvider")
  const { locale } = ctx
  return React.useMemo<Formatter>(
    () => ({
      dateTime: (date, options) =>
        new Intl.DateTimeFormat(locale, options).format(new Date(date)),
      number: (value, options) => new Intl.NumberFormat(locale, options).format(value),
    }),
    [locale],
  )
}
